import { KeyboardEvent, useCallback, useRef, useState } from 'react';
import { format } from 'date-fns';

import { cx } from '@libs/classnames';
import { DateView } from '@ui/components/DateView';
import { Input } from '@ui/components/Input';
import { ArrowIcon } from '@ui/icons/ArrowIcon/ArrowIcon';

import styles from './DateInput.module.css';

export type Props = {
  preset?: 'default' | 'heavy' | 'results';
  alignRight?: boolean;
  className?: string;
  invisible?: boolean;
  value?: Date | number | null;
  onChange: (newDate: Date | null) => void;
  disabled?: boolean;
  min?: string;
  max?: string;
  prefix?: string;
};
export function DateInput({
  preset,
  alignRight,
  className,
  value,
  onChange,
  invisible,
  disabled,
  min,
  max,
  prefix,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const handleOnFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);
  const handleOnBlur = useCallback(() => {
    setIsFocused(false);
    if (max && value && new Date(value) > new Date(max)) {
      onChange(new Date(max));
    } else if (min && value && new Date(value) < new Date(min)) {
      onChange(new Date(min));
    }
  }, [setIsFocused, onChange, min, max, value]);

  const handleInputChange = useCallback(
    (newValue?: string) => {
      onChange(newValue ? new Date(newValue) : null);
    },
    [onChange],
  );
  const handleWrapperClick = useCallback(() => {
    inputRef.current?.focus();

    if (preset === 'heavy') {
      inputRef.current?.showPicker();
    }
  }, [inputRef, preset]);

  const handleEnter = useCallback(
    (_: string, e: KeyboardEvent) => {
      if (!isFocused) return;
      e.stopPropagation();
      inputRef.current?.blur();
    },
    [isFocused],
  );

  const handlePickerClick = useCallback(() => {
    inputRef.current?.showPicker();
  }, []);

  return (
    <div
      className={cx(className, styles.root, {
        [styles.focus]: isFocused,
        [styles.disabled]: disabled,
        [styles.preset_heavy]: preset === 'heavy',
        [styles.preset_results]: preset === 'results',
      })}
      onClickCapture={handleWrapperClick}
    >
      {value && (
        <span className={cx(styles.value, { [styles.alignRight]: alignRight })}>
          {prefix}
          <DateView date={value} year="show" humanReadableDay={false} forceLeadingZero shortMonth />
        </span>
      )}
      <Input
        preset={preset}
        type="date"
        inputRef={inputRef}
        invisible={invisible}
        rightLabel={
          <div className={styles.pickerButton} onClick={handlePickerClick}>
            <ArrowIcon />
          </div>
        }
        className={styles.input}
        value={value ? format(value, 'yyyy-MM-dd') : ''}
        onChange={handleInputChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onEnter={handleEnter}
        disabled={disabled}
        min={min}
        max={max}
      />
    </div>
  );
}
